<template>
  <article class="phrase-card">
    <header class="phrase-card__header">
      <div class="avatar" :style="{ backgroundColor: getAvatarColor(phrase.author_username) }" aria-hidden="true">
        {{ authorInitial }}
      </div>
      <div class="author-block">
        <strong v-if="phrase.author_username">@{{ phrase.author_username }}</strong>
        <strong v-else>Noma'lum</strong>
        <span>{{ formatDate(phrase.date) }}</span>
      </div>
    </header>

    <div class="phrase-card__body">
      <h2>{{ phrase.title }}</h2>
      <p>{{ phrase.definition }}</p>

      <blockquote v-if="phrase.example">
        <span>Misol</span>
        <p>{{ phrase.example }}</p>
      </blockquote>
    </div>

    <div class="phrase-card__meta">
      <span v-if="phrase.tag" class="chip">
        <i class="bi bi-hash" aria-hidden="true"></i>
        {{ phrase.tag.name }}
      </span>
    </div>

    <div class="phrase-card__actions" aria-label="Ibora amallari">
      <button
        type="button"
        class="md-action"
        :class="{ active: userReaction === 'like' }"
        :disabled="isProcessing"
        :aria-pressed="userReaction === 'like'"
        @click="handleReaction('like')"
      >
        <i class="bi bi-hand-thumbs-up" aria-hidden="true"></i>
        <span>{{ phrase.like }}</span>
      </button>
      <button
        type="button"
        class="md-action"
        :class="{ active: userReaction === 'dislike' }"
        :disabled="isProcessing"
        :aria-pressed="userReaction === 'dislike'"
        @click="handleReaction('dislike')"
      >
        <i class="bi bi-hand-thumbs-down" aria-hidden="true"></i>
        <span>{{ phrase.dislike }}</span>
      </button>
      <button
        v-if="!hideDetailsLink"
        type="button"
        class="md-action tonal"
        @click="goToDetail"
      >
        <i class="bi bi-arrow-up-right" aria-hidden="true"></i>
        <span>Batafsil</span>
      </button>
      <button
        type="button"
        class="md-action"
        :disabled="isProcessing"
        @click="openReport"
      >
        <i class="bi bi-flag" aria-hidden="true"></i>
        <span>Shikoyat</span>
      </button>
    </div>

    <p v-if="message" class="supporting-text" :class="{ error: messageType === 'error' }" role="status">
      {{ message }}
    </p>

    <div v-if="showReportDialog" class="dialog-backdrop" role="presentation" @click.self="closeReport">
      <form class="report-dialog" role="dialog" aria-modal="true" aria-labelledby="report-title" @submit.prevent="submitReport">
        <h3 id="report-title">Shikoyat yuborish</h3>
        <label for="report-reason">Sabab</label>
        <textarea
          id="report-reason"
          v-model.trim="reportReason"
          rows="4"
          required
          placeholder="Qisqacha sabab yozing"
        ></textarea>
        <div class="dialog-actions">
          <button type="button" class="md-text-button" @click="closeReport">Bekor qilish</button>
          <button type="submit" class="md-filled-button" :disabled="isProcessing || !reportReason">Yuborish</button>
        </div>
      </form>
    </div>
  </article>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';

export default {
  name: 'DefinitionCard',
  props: {
    phrase: {
      type: Object,
      required: true,
    },
    hideDetailsLink: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userReaction: null,
      isProcessing: false,
      message: '',
      messageType: 'info',
      showReportDialog: false,
      reportReason: '',
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
    authorInitial() {
      return this.phrase?.author_username?.charAt(0).toUpperCase() || '?';
    },
  },
  mounted() {
    this.fetchReaction();
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format('DD.MM.YYYY');
    },
    getAvatarColor(username) {
      if (!username) return 'var(--md-sys-color-secondary)';
      let hash = 0;
      for (let i = 0; i < username.length; i += 1) {
        hash = username.charCodeAt(i) + ((hash << 5) - hash);
      }
      return `#${((hash >> 3) & 0xffffff).toString(16).padStart(6, '0')}`;
    },
    async fetchReaction() {
      try {
        const response = await axios.get(`phrases/phrases/${this.phrase.slug}/`);
        this.phrase.like = response.data.like;
        this.phrase.dislike = response.data.dislike;
        this.userReaction = response.data.user_reaction;
      } catch (error) {
        this.setMessage('Reaksiyalarni yuklab bo‘lmadi.', 'error');
      }
    },
    async handleReaction(type) {
      if (this.isProcessing) return;
      this.isProcessing = true;
      this.message = '';

      const opposite = type === 'like' ? 'dislike' : 'like';
      const wasReacted = this.userReaction === type;

      if (wasReacted) {
        this.phrase[type] = Math.max(0, this.phrase[type] - 1);
        this.userReaction = null;
      } else {
        if (this.userReaction === opposite) {
          this.phrase[opposite] = Math.max(0, this.phrase[opposite] - 1);
        }
        this.phrase[type] += 1;
        this.userReaction = type;
      }

      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.patch(`phrases/${this.phrase.slug}/reaction/`, { [type]: !wasReacted }, { headers });
      } catch (error) {
        this.setMessage('Reaksiya yuborilmadi. Tizimga kirganingizni tekshiring.', 'error');
        await this.fetchReaction();
      } finally {
        this.isProcessing = false;
      }
    },
    openReport() {
      if (!this.isAuthenticated) {
        this.setMessage('Shikoyat yuborish uchun avval tizimga kiring.', 'error');
        return;
      }
      this.reportReason = '';
      this.showReportDialog = true;
    },
    closeReport() {
      this.showReportDialog = false;
      this.reportReason = '';
    },
    async submitReport() {
      if (!this.reportReason) return;
      this.isProcessing = true;
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`phrases/phrases/${this.phrase.slug}/report/`, { reason: this.reportReason }, { headers });
        this.closeReport();
        this.setMessage('Shikoyatingiz qabul qilindi.', 'info');
      } catch (error) {
        this.setMessage('Shikoyat yuborilmadi. Qayta urinib ko‘ring.', 'error');
      } finally {
        this.isProcessing = false;
      }
    },
    setMessage(text, type = 'info') {
      this.message = text;
      this.messageType = type;
    },
    goToDetail() {
      this.$router.push({ name: 'PhraseDetail', params: { slug: this.phrase.slug } });
    },
  },
};
</script>

<style scoped>
.phrase-card {
  display: grid;
  gap: 20px;
  width: 100%;
  padding: 24px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-sys-shape-corner-large);
  color: var(--md-sys-color-on-surface);
  background: var(--md-sys-color-surface-container-low);
  box-shadow: var(--md-sys-elevation-1);
}

.phrase-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  display: grid;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 50%;
  color: white;
  font: var(--md-sys-typescale-label-large);
}

.author-block {
  display: grid;
  gap: 2px;
}

.author-block strong {
  color: var(--md-sys-color-on-surface);
  font: var(--md-sys-typescale-label-large);
}

.author-block span,
.supporting-text {
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-small);
}

.phrase-card__body h2 {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font: var(--md-sys-typescale-headline-medium);
}

.phrase-card__body > p {
  margin: 0;
  color: var(--md-sys-color-on-surface);
  font: var(--md-sys-typescale-body-large);
}

blockquote {
  margin: 18px 0 0;
  padding: 16px;
  border: 0;
  border-radius: var(--md-sys-shape-corner-medium);
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
}

blockquote span {
  display: block;
  margin-bottom: 4px;
  font: var(--md-sys-typescale-label-medium);
  text-transform: uppercase;
}

blockquote p {
  margin: 0;
  font: var(--md-sys-typescale-body-medium);
}

.phrase-card__meta,
.phrase-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid var(--md-sys-color-outline);
  border-radius: var(--md-sys-shape-corner-small);
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-label-large);
}

.md-action,
.md-text-button,
.md-filled-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px;
  border-radius: var(--md-sys-shape-corner-full);
  cursor: pointer;
  font: var(--md-sys-typescale-label-large);
}

.md-action {
  border: 1px solid var(--md-sys-color-outline);
  color: var(--md-sys-color-primary);
  background: transparent;
}

.md-action.active,
.md-action.tonal {
  border-color: transparent;
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
}

.md-action:disabled,
.md-filled-button:disabled {
  cursor: not-allowed;
  opacity: var(--md-sys-state-disabled-opacity);
}

.supporting-text {
  margin: 0;
}

.supporting-text.error {
  color: var(--md-sys-color-error);
}

.dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: color-mix(in srgb, var(--md-sys-color-scrim) 36%, transparent);
}

.report-dialog {
  display: grid;
  gap: 14px;
  width: min(440px, 100%);
  padding: 24px;
  border-radius: var(--md-sys-shape-corner-extra-large);
  color: var(--md-sys-color-on-surface);
  background: var(--md-sys-color-surface-container-high);
  box-shadow: var(--md-sys-elevation-3);
}

.report-dialog h3,
.report-dialog label {
  margin: 0;
}

.report-dialog textarea {
  width: 100%;
  min-height: 112px;
  padding: 14px 16px;
  border: 1px solid var(--md-sys-color-outline);
  border-radius: var(--md-sys-shape-corner-small);
  color: var(--md-sys-color-on-surface);
  background: var(--md-sys-color-surface-container-lowest);
  resize: vertical;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.md-text-button {
  border: 0;
  color: var(--md-sys-color-primary);
  background: transparent;
}

.md-filled-button {
  border: 0;
  color: var(--md-sys-color-on-primary);
  background: var(--md-sys-color-primary);
}

@media (max-width: 640px) {
  .phrase-card {
    padding: 18px;
  }

  .phrase-card__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .md-action {
    width: 100%;
  }
}
</style>
